import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { trackEvent } from "src/Amplitude";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import {
  CloseButton,
  Header,
  HeaderAndSearch,
  SearchBar,
  SearchBarIcon,
  SearchBarWrapper,
  Wrapper,
} from "src/components/common/crypto-selection-modalV2/CryptoSelectionModalV2";
import { TargetOrderHoldingsSkeleton } from "src/components/common/Skeletons";
import MagnifyingGlass from "src/icons/MagnifyingGlass";
import { useGetFiatAndStableCoinsQuery } from "src/store/features/api/generated";
import {
  SelectedCurrencyType,
  setCompletedFormSection,
  setSelectedCurrency,
  useTargetState,
} from "src/store/features/TargetOrders.slice";
import { useAppDispatch } from "src/store/store";
import { dynamicUnitFormatter, formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { TargetOrderStyles } from "../../styles/TargetOrder.style";

type SellDropDownProps = {
  userDefaultCurrency?: any;
};

export const TargetOrderFiatModal = ({ userDefaultCurrency }: SellDropDownProps) => {
  //----------------------Styles---------------------
  const classes = TargetOrderStyles();
  const theme = useTheme();
  //----------------------Redux---------------------
  const dispatch = useAppDispatch();
  const { orderType, selectedCurrency } = useTargetState();

  const { data: fiatCurrency = [], refetch, isLoading } = useGetFiatAndStableCoinsQuery();

  //----------------------State---------------------
  const [modal, setModal] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  //----------------------Hooks---------------------

  useEffect(() => {
    refetch();
  }, []);

  //----------------------Functions---------------------
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const handleCoinSelect = (event: React.MouseEvent, selectedCurrency: SelectedCurrencyType) => {
    const body: SelectedCurrencyType = {
      code: selectedCurrency.code,
      name: selectedCurrency.name,
      icon: selectedCurrency.icon,
      type: selectedCurrency.type,
      selected: true,
    };

    dispatch(setCompletedFormSection("currencyInput"));
    dispatch(setSelectedCurrency(body));
    handleModalToggle();
    trackEvent("Select_Target_Order_Currency", {
      Selected_Currency: selectedCurrency.code,
    });
  };
  const handleModalToggle = () => {
    setModal((prev) => !prev);
  };

  return (
    <>
      <Button
        disableRipple
        id="token-select-currency-btn-buy"
        onClick={handleModalToggle}
        sx={{
          height: "33px",
          borderRadius: "10px",
          padding: "8px",
          width: "auto",
          alignItems: "center",
          justifyContent: "normal !important",
          backgroundColor: selectedCurrency.code ? "#E8F6FF" : theme.palette.primary.main,
          "&:hover": { backgroundColor: " #4e7086" },
        }}
        endIcon={!selectedCurrency.code && <GridExpandMoreIcon sx={{ color: "#fff" }} />}>
        <Stack direction="row" justifyContent="center" alignItems="center">
          {selectedCurrency.code && (
            <Avatar style={{ backgroundColor: "transparent" }}>
              <CountryOrCryptoFlag
                name={selectedCurrency.icon || selectedCurrency.code}
                size={20}
              />
            </Avatar>
          )}
          <Typography
            sx={{
              color: selectedCurrency.code ? theme.palette.primary.main : "#fff",
              fontWeight: 600,
              lineHeight: "17px",
              fontSize: "14px",
              textTransform: "capitalize",
            }}>
            {selectedCurrency.code ? selectedCurrency.code : "Select Currency"}
          </Typography>
        </Stack>
      </Button>

      <Wrapper
        open={modal}
        onClose={handleModalToggle}
        sx={{ "& .MuiDialog-paper": { height: "fit-content" } }}>
        <HeaderAndSearch>
          {/* header */}
          <Header>
            {"Select Currency To Buy"}
            {/* close button */}
            <CloseButton onClick={handleModalToggle}>
              <CloseIcon fontSize="inherit" />
            </CloseButton>
          </Header>
          <SearchBarWrapper>
            <SearchBar
              autoFocus
              placeholder="Search"
              onChange={handleSearchChange}
              value={searchVal}
            />
            <SearchBarIcon>
              <MagnifyingGlass />
            </SearchBarIcon>
          </SearchBarWrapper>
        </HeaderAndSearch>

        <Grid container>
          <List className={classes.list} id="select-currency-list">
            {isLoading && !fiatCurrency.length && <TargetOrderHoldingsSkeleton />}
            {fiatCurrency
              ?.filter(
                (el) =>
                  el.name?.toLowerCase().includes(searchVal?.toLowerCase() ?? "") ||
                  el.code?.toLowerCase().includes(searchVal?.toLowerCase() ?? ""),
              )
              .map((el: any, idx: any) => (
                <Button
                  disableRipple
                  id={`select-buy-token-${el?.code}`}
                  fullWidth
                  key={idx}
                  onClick={(event) =>
                    handleCoinSelect(event, {
                      name: el.name,
                      code: el.code,
                      icon: el.logoUrl,
                      type: el.type,
                      selected: true,
                    })
                  }>
                  <Grid container>
                    <ListItem data-code={el.code} data-name={el.name}>
                      <ListItemAvatar>
                        <Avatar style={{ backgroundColor: "transparent" }}>
                          <CountryOrCryptoFlag name={el.logoUrl || el.code} size={35} />
                        </Avatar>
                      </ListItemAvatar>
                      <>
                        {" "}
                        {orderType[0] === "Buy" && (
                          <ListItemText
                            primary={el.name}
                            secondary={"Market Price"}
                            secondaryTypographyProps={{
                              color: "#9ca3a8 ",
                            }}
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                            sx={{
                              textTransform: "none",
                              flexBasis: "auto",
                            }}
                          />
                        )}
                        {orderType[0] === "Sell" && (
                          <ListItemText
                            primary={el.code}
                            secondary={el.name}
                            secondaryTypographyProps={{
                              color: "#9ca3a8 ",
                            }}
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontWeight: 700,
                            }}
                            sx={{
                              textTransform: "none",
                              flexBasis: "auto",
                            }}
                          />
                        )}
                        {orderType[0] === "Buy" && (
                          <ListItemText
                            sx={{
                              textTransform: "none",
                              textAlign: "end",
                            }}
                            secondaryTypographyProps={{
                              color: "#9ca3a8 ",
                            }}
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontWeight: 700,
                            }}
                            primary={dynamicUnitFormatter(el.available, 0) + " " + el.code}
                            secondary={formatCurrency(el.price, "usd")}
                          />
                        )}
                      </>
                    </ListItem>
                  </Grid>
                </Button>
              ))}
          </List>
        </Grid>
      </Wrapper>
    </>
  );
};
export default TargetOrderFiatModal;
